import {
  api,
  apiActionDispatcher,
  apiCall,
  idMapPairToDictionary,
  multiIdApiActionDispatcher,
  multiIdApiCall,
  multiIdRequestCacheCheck
} from './api';
import { jiraScansActionTypes, jiraStatusActionTypes } from './types';
import {
  getModalInfoByOwner,
  getRelativeMetricsByScanKeys,
  getShowSources
} from '../reducers';
import {
  actions as actionHelpers,
  ajax as ajaxHelpers,
  auth as authHelpers,
  messaging as messagingHelpers,
  modals as modalHelpers,
  storage as storageHelpers,
  urls as urlHelpers
} from '../helpers';
import {
  auth as authActions,
  modal as modalActions
} from '../actions';
import { toastr } from 'react-redux-toastr';
import { trackPromise } from 'react-promise-tracker';

const fetchJiraScansDispatcher = apiActionDispatcher({
  actionTypes: jiraScansActionTypes,
  errorMessage: messagingHelpers.AUTH_GENERIC_FAILED
});

export const fetchJiraScans = (authToken, step = 50) => async (dispatch, getState) => {
  if (!authToken) {
    return false;
  }

  // Get number of issues
  let length = 0;
  const promises = [];

  try {
    length = await trackPromise(api.jira.fetchScanCount(authToken));
  }
  catch (error) {
    authActions.authErrorFunc(error);
  }

  try {
    let i = 0;

    while (i < length) {
      promises.push(apiCall(
        fetchJiraScansDispatcher,
        api.jira.fetchScans,
        null, // No caching for this call
        null,
        null,
        [authToken, step, i],
        null,
        authActions.authErrorFunc
      )(dispatch, getState));
      i += step;
    }
  }
  catch (error) {
    console.error(error);
  }

  const responses = await Promise.allSettled(promises);

  const flattenedResponses = [].concat.apply(
    [],
    responses.map(r => r.status === 'fulfilled' && r.value)
  );

  if (flattenedResponses.some(r => !r)) {
    toastr.warning(
      'Incomplete SCANs retrieval',
      'Some SCAN tickets may be missing, please reload as needed to retry retrieval.',
      { timeOut: 0 });
  }

  const fulfilledResponses = flattenedResponses.filter(r => r);
  return fulfilledResponses.every(r => r === true);
};

const fetchJiraStatusDispatcher = multiIdApiActionDispatcher({
  actionTypes: jiraStatusActionTypes,
  accumulatorIdProp: 'name',
  responseHandler: byId => {
    const results = idMapPairToDictionary(byId);
    for (const key in results) results[key] = results[key][key];
    return results;
  },
  errorMessage: messagingHelpers.AUTH_GENERIC_FAILED
});
export const fetchJiraStatus = (authToken, tickets) =>
  multiIdApiCall(
    fetchJiraStatusDispatcher,
    api.jira.ticketStatuses,
    (state, ids) => multiIdRequestCacheCheck(state.jiraStatuses.request, ids),
    tickets,
    [authToken],
    authActions.authErrorFunc
  );

export const refreshScans = () => async dispatch => {
  const authToken = authHelpers.getAuthToken();
  await dispatch(fetchJiraScans(authToken));
};

export const confirmJiraDialog = () => async (dispatch, getState) => {
  const state = getState();
  const showSources = getShowSources(state);
  const jiraModalInfo = getModalInfoByOwner(state, modalHelpers.owners.jiraConfirm);

  if (jiraModalInfo.scanKeys.length === 0) {
    return;
  }

  const metrics = getRelativeMetricsByScanKeys(state, jiraModalInfo.scanKeys);

  if (metrics.length === 0) {
    return;
  }

  const art = jiraModalInfo.artifact ? jiraModalInfo.artifact : undefined;
  const delta = jiraModalInfo.delta ? jiraModalInfo.delta : undefined;
  toastr.info('Updating', 'Updating scans...', { timeOut: 0 });
  const authToken = authHelpers.getAuthToken();

  try {
    const promises = metrics.map(async m => {
      const changes = [
        jiraModalInfo.typesChecked.ncfHits ?
          actionHelpers.getHitCountChanges(m, 'ncf', art, delta) :
          { newValue: m['ncf'].artHitCounts.baseline, changes: [], type: 'ncf' },
        jiraModalInfo.typesChecked.ccfHits ?
          actionHelpers.getHitCountChanges(m, 'ccf', art, delta) :
          { newValue: m['ccf'].artHitCounts.baseline, changes: [], type: 'ccf' },
        jiraModalInfo.typesChecked.ncfDuration ?
          actionHelpers.getDurationChanges(m.ncf, 'ncf') :
          { newValue: m['ncf'].durationMetrics.baseline, changes: [], type: 'ncf' },
        jiraModalInfo.typesChecked.ccfDuration ?
          actionHelpers.getDurationChanges(m.ccf, 'ccf') :
          { newValue: m['ccf'].durationMetrics.baseline, changes: [], type: 'ccf' },
        jiraModalInfo.typesChecked.ncfSource && showSources ?
          actionHelpers.getSourceCountChanges(m.ncf) :
          { newValue: m['ncf'].sourceCountMetrics.baseline, changes: [], type: 'ncf' },
        jiraModalInfo.typesChecked.ncfSourceAttribute && showSources ?
          actionHelpers.getSourceAttributeCountChanges(m.ncf) :
          {
            newValue: m['ncf'].sourceAttributeCountMetrics.baseline,
            changes: [],
            type: 'ncf'
          }
      ];

      changes.forEach(c => {
        if (c.changes.length === 0) {
          return;
        }

        fireHitCountChangeComment(c, jiraModalInfo.issue, authToken, m.scanKey);
      });

      return await api.jira
        .updateExpectedHits(
          m.scanKey,
          {
            ncf: changes[0].newValue,
            ccf: changes[1].newValue
          },
          changes[2].newValue,
          changes[3].newValue,
          {
            ncf: changes[4].newValue,
            ccf: {}
          },
          {
            ncf: changes[5].newValue,
            ccf: {}
          },
          authToken
        )
        .then(httpStatusCode => {
          // dont want to block comment post since timeouts results
          // are unknown, hitcount may or may not have updated
          // only displays warning
          if (httpStatusCode !== ajaxHelpers.HTTPSTATUSCODE.OK) {
            toastr.warning(
              'Update to JIRA may have encountered a problem',
              'Please verify hitcounts were correctly pushed to SCAN ticket',
              { timeOut: 0 }
            );
          }
        });
    });

    dispatch(modalActions.showJiraConfirmModal(false, [], {}));

    await Promise.all(promises);
    await dispatch(fetchJiraScans(authToken));
  } finally {
    toastr.removeByType('info');
  }
};

const fireHitCountChangeComment = (changeInfo, issue, authToken, scanKey) => {
  const change = changeInfo.changes.sort().join(',');
  const cause =
    (issue && issue.length > 0 ? `${urlHelpers.JIRA_BASE_URL}/browse/` : '') + issue;
  const type = changeInfo.type;
  const delta = changeInfo.delta;

  const comment = [
    `On behalf of logged in user ${localStorage.getItem(storageHelpers.USER_KEY)}:`,
    `${type.toUpperCase()} hit count change accepted from build dashboard` +
      (cause.length > 0 ? ` due to ${cause}` : ''),
    `Total hit count ${delta.diff > 0 ? '+' : ''}${delta.diff} from ${delta.old} ` +
      `to ${delta.new}`,
    change
  ].join(',');

  api.jira.addComment(scanKey, encodeURIComponent(comment), authToken);
};



// WEBPACK FOOTER //
// src/actions/jira.js